const USER_KEY = "user";
const AUTH_URL_PARTS = ["/user/signin", "/user/signup"];

export function getStoredUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function saveSession(user) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  window.dispatchEvent(new Event("user-changed"));
}

export function clearSession() {
  localStorage.removeItem(USER_KEY);
  window.dispatchEvent(new Event("user-changed"));
}

export function getAuthToken() {
  return getStoredUser()?.token || "";
}

function parseExpiry(value) {
  if (!value) return NaN;
  const raw = String(value).trim();
  const isoish = raw.includes("T") ? raw : raw.replace(" ", "T");
  return Date.parse(isoish);
}

export function isSessionExpired(user = getStoredUser()) {
  if (!user?.token) return true;
  if (!user.expires_at) return false;
  const ts = parseExpiry(user.expires_at);
  if (Number.isNaN(ts)) return false;
  return ts <= Date.now();
}

export function isAuthenticated() {
  const user = getStoredUser();
  return Boolean(user?.token) && !isSessionExpired(user);
}

export function dashboardPathForRole(role) {
  if (role === "firm") return "/firm/dashboard";
  if (role === "lawyer") return "/lawyer/dashboard";
  if (role === "team") return "/team/dashboard";
  return "/admin/dashboard";
}

export function safePostLoginPath(next, role) {
  const fallback = dashboardPathForRole(role);
  if (!next || typeof next !== "string") return fallback;
  if (!next.startsWith("/") || next.startsWith("//")) return fallback;
  if (role === "firm" && next.startsWith("/firm")) return next;
  if (role === "lawyer" && next.startsWith("/lawyer")) return next;
  if (role === "team" && next.startsWith("/team")) return next;
  if ((role === "admin" || !role) && next.startsWith("/admin")) return next;
  return fallback;
}

export function isAuthRequest(url = "") {
  const value = String(url);
  return AUTH_URL_PARTS.some((part) => value.includes(part));
}

export function isUnauthorizedError(err) {
  const status = err?.response?.status;
  const body = err?.response?.data;
  const code = body?.status_code;
  const message = String(body?.message || err?.message || "").toLowerCase();
  if (status === 401 || code === 401 || body?.code === "TOKEN_EXPIRED") return true;
  return (
    message.includes("token required") ||
    message.includes("invalid or expired token") ||
    message.includes("expired token")
  );
}

export function updateStoredExpiry(expiresAt) {
  if (!expiresAt) return;
  const user = getStoredUser();
  if (!user?.token || user.expires_at === expiresAt) return;
  localStorage.setItem(USER_KEY, JSON.stringify({ ...user, expires_at: expiresAt }));
}

let redirecting = false;

export function handleUnauthorized() {
  if (typeof window === "undefined") return;
  const path = window.location.pathname || "";
  if (redirecting || path.startsWith("/login")) return;

  redirecting = true;
  clearSession();

  const next = `${path}${window.location.search || ""}`;
  const params = next && next !== "/" ? `?next=${encodeURIComponent(next)}` : "";
  window.location.assign(`/login${params}`);
}
